export type UnitBandKey = 'studio' | '1br' | '2br' | '3br' | '4br+';

export type UnitBand = {
  /** English label (`Studio · from 1.2M`) — kept for keys/non-display use; UIs translate via `key` + `priceLabel`. */
  label: string;
  key: UnitBandKey;
  /** Fuzzy price, e.g. `1.2M` / `850K`. */
  priceLabel: string;
  minPrice: number;
};

type ApartmentInput = {
  bedrooms?: number | string | null;
  type?: string | null;
  unitType?: string | null;
  price?: number | string | null;
  priceMin?: number | string | null;
  startingPrice?: number | string | null;
  minPrice?: number | string | null;
} & Record<string, unknown>;

const DEFAULT_BUCKETS: { key: UnitBandKey; label: string; match: (a: ApartmentInput) => boolean }[] = [
  { key: 'studio', label: 'Studio', match: (a) => bedroomsOf(a) === 0 },
  { key: '1br', label: '1BR', match: (a) => bedroomsOf(a) === 1 },
  { key: '2br', label: '2BR', match: (a) => bedroomsOf(a) === 2 },
  { key: '3br', label: '3BR', match: (a) => bedroomsOf(a) === 3 },
  { key: '4br+', label: '4BR+', match: (a) => bedroomsOf(a) >= 4 },
];

function bedroomsOf(a: ApartmentInput): number {
  const raw = a.bedrooms ?? a.type ?? a.unitType ?? '';
  if (typeof raw === 'number') return raw;
  const str = String(raw).toLowerCase();
  if (/studio/.test(str)) return 0;
  const match = str.match(/(\d+)/);
  return match ? Number(match[1]) : -1;
}

function priceOf(a: ApartmentInput): number {
  const candidates = [a.priceMin, a.minPrice, a.startingPrice, a.price];
  for (const v of candidates) {
    if (v == null) continue;
    const n = typeof v === 'number' ? v : Number(String(v).replace(/[^\d.]/g, ''));
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
}

function fuzzyAedLabel(price: number): string {
  if (price >= 1_000_000) {
    const m = Math.floor(price / 100_000) / 10;
    return `${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
  }
  if (price >= 1_000) {
    const k = Math.floor(price / 10_000) * 10;
    return `${k}K`;
  }
  return String(Math.floor(price));
}

export function fuzzyUnitBands(apartments: unknown[] | null | undefined, options?: { maxBands?: number }): UnitBand[] {
  const list = Array.isArray(apartments) ? (apartments as ApartmentInput[]) : [];
  if (!list.length) return [];

  const maxBands = options?.maxBands ?? 5;
  const bands: UnitBand[] = [];

  for (const bucket of DEFAULT_BUCKETS) {
    const inBucket = list
      .filter((a) => bucket.match(a))
      .map(priceOf)
      .filter((p) => p > 0);
    if (!inBucket.length) continue;
    const min = Math.min(...inBucket);
    const priceLabel = fuzzyAedLabel(min);
    bands.push({
      label: `${bucket.label} · from ${priceLabel}`,
      key: bucket.key,
      priceLabel,
      minPrice: min,
    });
  }

  return bands.slice(0, maxBands);
}
